import React, { use, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useGlobalStore from '../../hooks/useGlobalStore';
import { CallWithAuth } from '../../actions/apiAction';
import { CREATE_CHECKOUT_SESSION, GET_SUBSCRIPTION_PLANS, GET_SUBSCRIBED_CREATOR_BY_ID, RESUME_SUBSCRIPTION_BY_ID } from '../../utils/apiUrl';
import toast from 'react-hot-toast';
import { trackEvent } from '../../utils/analytics';
import './UpgradeSubscriptionModal.scss';

const UpgradeSubscriptionModal = ({
    creatorId,
    creatorDetails: userDetails,
    subscriptionLimit,
    openFrom,
    canAutoOpen,
}) => {
    const { TOGGLE_UPGRADE_SUBSCRIPTION, toggleForModal, offForModal, onForModal } = useGlobalStore();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([])
    const [addonPlans, setAddonPlans] = useState([])
    const [subscriptionDetail, setSubscriptionDetail] = useState(null);
    const [dragStart, setDragStart] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [actionLoadingPlanId, setActionLoadingPlanId] = useState(null);
    const lock = subscriptionLimit?.user_type == "unsubscribed" ? true : false
    const isTrial = subscriptionLimit?.user_type === "trial"
    // Creator hasn't finished payout onboarding — checkout would reject this,
    // so preview the plans instead of letting the fan tap through to a failure.
    // Only an explicit `false` locks; a missing field keeps today's behaviour.
    const comingSoon = userDetails?.accepting_subscriptions === false;
    // Lowercase fallback so it reads correctly mid-sentence; the headings that
    // lead with it use first-letter:uppercase.
    const creatorName =
        userDetails?.first_name || userDetails?.username || 'this creator';

    // Nothing is buyable in this state, so closing in place would strand the fan
    // on a chat they can't use. Send them home instead — chat still requires a
    // subscription, which is why this navigates rather than just dismissing.
    const leaveComingSoon = () => {
        offForModal('TOGGLE_UPGRADE_SUBSCRIPTION');
        navigate('/user/creator-list');
    };

    // The paywall hides the drag handle so unsubscribed fans can't dismiss it,
    // but coming-soon fans need the way out.
    const dragLocked = lock && !comingSoon;

    const handleClose = () => {
        // Coming-soon overrides the paywall lock: there is nothing to buy, so
        // dismissing has to be allowed — but it leaves the chat rather than
        // dropping the fan onto an unusable one.
        if (comingSoon) return leaveComingSoon();
        if (lock) return;
        offForModal('TOGGLE_UPGRADE_SUBSCRIPTION');
    };
    const handleNotClose = () => console.log("Not Close Modal")

    const goToBillingHistory = () => {
        handleClose();
        navigate(`/user/billing-history?creator_id=${creatorId}`);
    };

    const handleDragStart = (e) => {
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setDragStart(clientY);
    };

    const handleDragMove = (e) => {
        if (dragStart === 0) return;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const offset = clientY - dragStart;
        if (offset > 0) setDragOffset(offset);
    };

    const handleDragEnd = () => {
        if (!dragLocked && dragOffset > 100) {
            handleClose();
        }
        setDragStart(0);
        setDragOffset(0);
    };

    const getSubscriptionList = async () => {
        const [plansRes, addonsRes, subRes] = await Promise.all([
            CallWithAuth("GET", GET_SUBSCRIPTION_PLANS(creatorId)),
            CallWithAuth("GET", GET_SUBSCRIPTION_PLANS(creatorId, 'add-on')),
            CallWithAuth("GET", GET_SUBSCRIBED_CREATOR_BY_ID(creatorId)),
        ]);
        if (plansRes?.data) setPlans(plansRes.data);
        if (addonsRes?.data) setAddonPlans(Array.isArray(addonsRes.data) ? addonsRes.data : []);
        // 404 just means the user has never subscribed to this creator.
        setSubscriptionDetail(subRes?.status === 200 ? subRes.data : null);
    };
    useEffect(() => {
        getSubscriptionList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const createCheckoutSession = async (planId) => {
        if (comingSoon) return;
        setActionLoadingPlanId(planId);
        try {
            const response = await CallWithAuth("POST", CREATE_CHECKOUT_SESSION,
                { plan_id: planId, creator_id: creatorId }
            );
            if (!response) {
                toast.error("Couldn't reach the server. Please check your connection and try again.");
                return;
            }
            if (response.status === 200) {
                if (response?.data?.checkout_url) {
                    trackEvent('checkout_initiated', {
                        creator_id: creatorId || undefined,
                        surface: openFrom || 'upgrade',
                    });
                    window.location.href = response?.data?.checkout_url
                } else {
                    toast.error("Something went wrong!")
                }
            } else {
                toast.error(response?.data?.detail || "Something went wrong!")
            }
        } finally {
            setActionLoadingPlanId(null);
        }
    }

    const reactivatePlan = async (subscriptionId, planId) => {
        if (!subscriptionId) return;
        setActionLoadingPlanId(planId);
        try {
            const response = await CallWithAuth("POST", RESUME_SUBSCRIPTION_BY_ID(subscriptionId));
            if (!response) {
                toast.error("Couldn't reach the server. Please check your connection and try again.");
                return;
            }
            if (response.status === 200) {
                toast.success('Subscription reactivated');
                await getSubscriptionList();
            } else {
                toast.error(response?.data?.detail || "Something went wrong!")
            }
        } finally {
            setActionLoadingPlanId(null);
        }
    }

    const formatShortDate = (value) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    const formatMinutesToClock = (value) => {
        const minutes = Number(value);
        if (Number.isNaN(minutes)) return '00:00';

        const totalSeconds = Math.max(0, Math.round(minutes * 60));
        const wholeMinutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        return `${String(wholeMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };
    const formatMessageCount = (value) => {
        const count = Number(value);
        if (Number.isNaN(count)) return '0';
        return String(Math.max(0, Math.floor(count)));
    };
    const formatInterval = (interval) => (interval === 'one_time' ? 'one-time' : interval);

    const hasAnySubscription = Boolean(
        subscriptionDetail && (
            subscriptionDetail.status === 'active' ||
            (subscriptionDetail.status === 'cancelled' && (
                !subscriptionDetail.current_period_end ||
                new Date(subscriptionDetail.current_period_end) > new Date()
            ))
        )
    );
    const visibleAddons = addonPlans.filter((pack) => {
        if (!hasAnySubscription) return false;
        if (openFrom === 'voice') return (pack.limits?.voice_minutes || 0) > 0;
        if (openFrom === 'video') return (pack.limits?.video_minutes || 0) > 0;
        return true;
    });

    const paywallTracked = useRef(false);
    useEffect(() => {
        if (!TOGGLE_UPGRADE_SUBSCRIPTION) {
            paywallTracked.current = false;
            return;
        }
        if (paywallTracked.current) return;
        paywallTracked.current = true;
        trackEvent('paywall_displayed', {
            creator_id: creatorId || undefined,
            surface: openFrom || 'upgrade',
        });
    }, [TOGGLE_UPGRADE_SUBSCRIPTION, creatorId, openFrom]);

    useEffect(() => {
        if (!canAutoOpen) return; // NEW — wait for onboarding to be checked/dismissed first
        if (subscriptionLimit?.user_type == "unsubscribed") {
            onForModal('TOGGLE_UPGRADE_SUBSCRIPTION')
        }
    }, [subscriptionLimit, canAutoOpen])
    return (
        <>
            {/* start: upgrade-overlay */}

            {TOGGLE_UPGRADE_SUBSCRIPTION && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-40"
                    onClick={handleClose}
                />
            )}
            <div
                className={`upgrade-subscription-modal fixed bottom-0 left-0 right-0 rounded-t-3xl shadow-2xl z-50 h-[85%] flex flex-col overflow-hidden
                 transition-all ${dragStart === 0 ? 'duration-300' : 'duration-0'} ${TOGGLE_UPGRADE_SUBSCRIPTION && dragStart === 0 ? '!translate-y-[0]' : 'translate-y-full'}`}
                style={{ transform: dragStart !== 0 && !dragLocked ? `translateY(${dragOffset}px)` : undefined }}
            >
                <div
                    className={`upgrade-modal-header flex justify-center items-center shrink-0 ${dragLocked ? '' : 'cursor-grab active:cursor-grabbing'}`}
                    onMouseDown={dragLocked ? undefined : handleDragStart}
                    onMouseMove={dragLocked ? undefined : handleDragMove}
                    onMouseUp={dragLocked ? undefined : handleDragEnd}
                    onMouseLeave={dragLocked ? undefined : handleDragEnd}
                    onTouchStart={dragLocked ? undefined : handleDragStart}
                    onTouchMove={dragLocked ? undefined : handleDragMove}
                    onTouchEnd={dragLocked ? undefined : handleDragEnd}
                >
                    {!dragLocked && <div className="w-12 h-1 bg-border rounded-full" />}
                </div>
                <div className="upgrade-modal-content flex-1 min-h-0 overflow-y-auto scrollbar-hide p-6 pb-8">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width={24}
                                height={24}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-lock w-8 h-8 text-primary"
                            >
                                <rect width={18} height={11} x={3} y={11} rx={2} ry={2} />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground mb-2 first-letter:uppercase">
                            {comingSoon
                                ? `${creatorName} isn't ready for subscribers yet`
                                : openFrom === 'paywall' || lock
                                    ? `Subscribe to chat with ${userDetails?.first_name ?? "them"}`
                                    : isTrial && (openFrom === 'voice' || openFrom === 'video')
                                        ? `Subscribe to call ${userDetails?.first_name ?? "them"}`
                                        : openFrom === 'voice'
                                            ? `Voice call limit reached`
                                            : openFrom === 'video'
                                                ? `Video call limit reached`
                                                : `Keep talking to ${userDetails?.first_name ?? ""}`
                            }
                        </h2>
                        <p className="text-foreground/70 text-sm">
                            {comingSoon
                                ? `They're still finishing setup, so subscriptions aren't open yet. Here's what will be available — check back soon!`
                                : openFrom === 'paywall' || lock
                                    ? `Choose a plan to unlock messages, voice, and video with ${userDetails?.first_name ?? "this creator"}.`
                                    : isTrial && (openFrom === 'voice' || openFrom === 'video')
                                        ? `Voice and video calls aren't included in the free trial. Choose a plan to talk with ${userDetails?.first_name ?? "this creator"}.`
                                        : openFrom === 'voice'
                                            ? hasAnySubscription
                                                ? `You've used up your voice minutes. Add more to keep talking to ${userDetails?.first_name ?? ""}`
                                                : `You've used up your voice minutes. Upgrade to continue talking to ${userDetails?.first_name ?? ""}`
                                            : openFrom === 'video'
                                                ? hasAnySubscription
                                                    ? `You've used up your video minutes. Add more to keep talking to ${userDetails?.first_name ?? ""}`
                                                    : `You've used up your video minutes. Upgrade to continue talking to ${userDetails?.first_name ?? ""}`
                                                : `Upgrade and keep hitting it off with your favorite creator!`
                            }
                        </p>



                    </div>
                    <div className="upgrade-plan-list gap-y-6 mb-6 flex flex-col">
                        {
                            plans.length > 0 &&
                            plans.map((it, ind) => {
                                const currentPlan = plans.find((p) => p.is_current_plan);
                                // Mirrors the backend's access_granting_filter: "cancelled" still
                                // counts as subscribed until current_period_end actually passes.
                                const hasAnySubscription = Boolean(
                                    subscriptionDetail && (
                                        subscriptionDetail.status === 'active' ||
                                        (subscriptionDetail.status === 'cancelled' && (
                                            !subscriptionDetail.current_period_end ||
                                            new Date(subscriptionDetail.current_period_end) > new Date()
                                        ))
                                    )
                                );
                                const isEndingSoon = it?.is_current_plan && hasAnySubscription && subscriptionDetail?.status === 'cancelled';
                                const isBusy = actionLoadingPlanId === it?.id;
                                const isUpgrade = Boolean(currentPlan) && Number(it?.price) > Number(currentPlan.price);
                                return (
                                    <div
                                        className={`upgrade-plan ${it?.is_current_plan ? (isEndingSoon ? "is-ending" : "is-active") : it?.name == "Premium" ? "is-premium" : ""}`}
                                        style={{ opacity: 1, transform: "none" }}
                                        key={it?.id}
                                    >
                                        {
                                            it?.is_current_plan &&
                                            <div className={isEndingSoon ? "absolute -top-4 left-2 md:left-1/2 transform md:-translate-x-1/2 bg-amber-500 text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg" : "absolute -top-4 left-2 md:left-1/2 transform md:-translate-x-1/2 bg-primary text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg"}>
                                                {isEndingSoon
                                                    ? `Active${formatShortDate(subscriptionDetail?.current_period_end) ? ` till ${formatShortDate(subscriptionDetail?.current_period_end)}` : ''}`
                                                    : 'Active'}
                                            </div>
                                        }
                                        {
                                            !it?.is_current_plan && it?.best_value == true &&

                                            <div className="absolute -top-4 left-2 md:left-1/2 transform md:-translate-x-1/2 bg-primary text-primary-foreground px-3 md:min-w-[194px] py-1.5 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width={24}
                                                    height={24}
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="#fff"
                                                    strokeWidth={2}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="lucide lucide-star w-4 h-4"
                                                >
                                                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                                                </svg>
                                                Best Value
                                            </div>
                                        }
                                        {
                                            !it?.is_current_plan && it?.discount_percentage &&
                                            <div className="absolute -top-4 right-2 right-md-4 bg-[#5f338b] text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">
                                                {it?.discount_percentage}% OFF
                                            </div>
                                        }
                                        <div className="flex justify-between items-start mb-3 mt-2">
                                            <div>
                                                <h3 className="text-xl font-bold text-foreground">{it?.name}</h3>
                                                <p className="text-xs text-muted-foreground">
                                                    {it?.description}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                {it?.slashed_price && <p className="text-sm text-muted-foreground line-through">${Number(it?.slashed_price).toFixed(2)}</p>}
                                                <p className="text-3xl font-bold text-primary">${it?.price}</p>
                                                <p className="text-xs text-muted-foreground">{formatInterval(it?.interval)}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-1 text-sm text-foreground/80 mb-4">
                                            {
                                                it?.marketing_features?.length > 0 &&
                                                it?.marketing_features?.map((item, index) => {
                                                    return <p className="flex items-center gap-2" key={index}>
                                                        {
                                                            index == 0 ?
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width={24}
                                                                    height={24}
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="#d7428c"
                                                                    strokeWidth={2}
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="lucide lucide-sparkles w-4 h-4 text-primary"
                                                                >
                                                                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                                                                    <path d="M20 3v4" />
                                                                    <path d="M22 5h-4" />
                                                                    <path d="M4 17v2" />
                                                                    <path d="M5 18H3" />
                                                                </svg> : <span>•</span>
                                                        }
                                                        {item}</p>
                                                })

                                            }

                                        </div>
                                        {
                                            it?.is_current_plan ?
                                                isEndingSoon ?
                                                    <button
                                                        disabled={isBusy}
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-primary text-primary-foreground hover:opacity-90 transition-all duration-300 disabled:opacity-50"
                                                        onClick={() => reactivatePlan(subscriptionDetail?.subscription_id, it?.id)}
                                                    >
                                                        {isBusy ? 'Reactivating…' : 'Reactivate'}
                                                    </button>
                                                    :
                                                    <button
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-primary text-success border border-success/40 shadow-sm shadow-success/20 hover:bg-success/25 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                                                        onClick={goToBillingHistory}
                                                    >
                                                        Manage
                                                    </button>
                                                : hasAnySubscription ?
                                                    <button
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-white text-primary border border-primary/40 shadow-sm shadow-primary/20 hover:bg-primary/10 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                                                        onClick={goToBillingHistory}
                                                    >
                                                        {isUpgrade ? 'Upgrade' : 'Manage Subscription'}
                                                    </button>
                                                    : comingSoon ?
                                                        <button
                                                            disabled
                                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-6 text-lg font-semibold w-full bg-white/10 text-white/70 border border-white/20 cursor-not-allowed"
                                                        >
                                                            🔒 Coming soon
                                                        </button>
                                                        :
                                                        <button
                                                            disabled={isBusy}
                                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 bg-primary text-primary-foreground hover:opacity-90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 w-full"
                                                            onClick={() => createCheckoutSession(it?.id)}
                                                        >
                                                            {isBusy ? 'Redirecting…' : it?.name == "Premium" ? 'Get Premium' : `Continue with ${it?.name}`}
                                                        </button>
                                        }
                                    </div>
                                )
                            })
                        }
                    </div>
                    {hasAnySubscription && visibleAddons.length > 0 && !comingSoon && (
                        <div className="upgrade-plan-list gap-y-6 mb-6 flex flex-col">
                            <p className="text-sm font-semibold text-white text-center">
                                Need more time with {creatorName}?
                            </p>
                            <p className="text-xs text-white/70 text-center -mt-2">
                                Get extra voice and video time for this billing period
                            </p>
                            {visibleAddons.map((pack) => {
                                const isBusy = actionLoadingPlanId === pack.id;
                                return (
                                    <div
                                        className="upgrade-plan"
                                        style={{ opacity: 1, transform: "none" }}
                                        key={pack.id}
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-5 mt-1">
                                            <h3 className="text-lg font-bold leading-snug text-foreground flex-1 min-w-0">
                                                {pack.name}
                                            </h3>
                                            <div className="text-right shrink-0">
                                                <p className="text-2xl font-bold leading-none text-primary">${pack.price}</p>
                                                <p className="text-xs text-muted-foreground mt-1">{formatInterval(pack.interval)}</p>
                                            </div>
                                        </div>
                                        <button
                                            disabled={isBusy}
                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-primary/90 h-10 bg-primary text-primary-foreground hover:opacity-90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 w-full"
                                            onClick={() => createCheckoutSession(pack.id)}
                                        >
                                            {isBusy ? 'Redirecting…' : 'Get'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {comingSoon ? (
                        <>
                            <p className="text-xs text-center text-white px-4">
                                We'll let you know as soon as {creatorName} is ready. Nothing has
                                been charged.
                            </p>
                            {/* Nothing here is buyable, so an explicit exit beats
                                relying on the drag gesture alone. Leaves the chat
                                rather than just closing, since chat still needs a
                                subscription. */}
                            <button
                                type="button"
                                className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-4 text-base font-semibold w-full bg-white/10 text-white border border-white/20 hover:bg-white/15 transition-all duration-200"
                                onClick={leaveComingSoon}
                            >
                                Go back
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="text-xs text-center text-white px-4">
                                By continuing, you'll unlock continued access to {creatorName}. You can
                                cancel anytime from your account settings.
                            </p>
                            <button
                                type="button"
                                className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-8 py-4 text-base font-semibold w-full bg-white/10 text-white border border-white/20 hover:bg-white/15 transition-all duration-200"
                                onClick={leaveComingSoon}
                            >
                                Not Now
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>

    );
}

export default UpgradeSubscriptionModal;
